import { useMemo } from "react";
import { useQuery } from "@tanstack/react-query";
import { cardsAPI } from "../api/modules/cards.module";

// Query key factory
export const cartListingKeys = {
  all: ["cart-listings"],
  list: (ids) => [...cartListingKeys.all, "list", [...ids].sort((a, b) => a - b)],
};

// Hook to fetch fresh cart listing data (presigned image URLs + current
// price/stock) by id, so the cart never renders stale cached data.
export const useCartListings = (ids = []) => {
  const uniqueIds = Array.from(new Set((ids || []).filter((id) => id != null)));

  const query = useQuery({
    queryKey: cartListingKeys.list(uniqueIds),
    queryFn: async () => {
      const response = await cardsAPI.getListingsByIds(uniqueIds);
      if (!response.success) {
        throw new Error(response.message || "Failed to load cart listings");
      }
      return response.listings || [];
    },
    enabled: uniqueIds.length > 0,
    staleTime: 5 * 60 * 1000,
    refetchOnMount: "always",
    retry: 1,
  });

  // Memoised on the query data, NOT rebuilt per render. `listingsById` is a
  // dependency of the useMemo behind `useCartWithListings().cartData`, and
  // Checkout re-hydrates its handoff in an effect keyed on `cartData` — an
  // effect that calls setCheckoutData. A fresh object here therefore re-runs
  // that effect on the render it just caused: "Maximum update depth exceeded"
  // on /checkout.
  const listingsById = useMemo(() => {
    const byId = {};
    for (const listing of query.data || []) {
      byId[listing.id] = listing;
    }
    return byId;
  }, [query.data]);

  return {
    listingsById,
    isLoading: uniqueIds.length > 0 && query.isLoading,
    isError: query.isError,
    refetch: query.refetch,
  };
};
